import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import dayjs from "dayjs";
import { PackageRegistrationInjection } from "features/packageRegistration/di/PackageRegistrationInjection";
import { PackageRegistrationQueryEntity } from "features/packageRegistration/domain/entities/PackageRegistrationEntity";
import { PackageRegistrationItemEntity } from "features/packageRegistration/domain/entities/PackageRegistrationItemEntity";
import isEmpty from "lodash/isEmpty";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { strings } from "utils/localizedStrings";
import { IPackageRegistrationObservedState } from "../interfaces/IPackageRegistration";

export interface IDropdownActionRecord {
  record: PackageRegistrationItemEntity;
  action: string;
}

export type DetailSelectionType =
  | PackageRegistrationItemEntity
  | IDropdownActionRecord;

type FilterOptionsState = Pick<
  IPackageRegistrationObservedState,
  "packageList" | "storeOptions" | "staffOptions" | "userOptions"
>;

class PackageRegistrationInput implements Disposable {
  public fetchList = new BehaviorSubject<PackageRegistrationQueryEntity>(
    new PackageRegistrationQueryEntity()
  );
  public fetchCarrier = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchPackage = new BehaviorSubject<BaseFilterListRequestModel>({});
  public exportExcel = new BehaviorSubject<PackageRegistrationQueryEntity>(
    new PackageRegistrationQueryEntity()
  );
  public fetchDetail = new BehaviorSubject<PackageRegistrationQueryEntity>(
    new PackageRegistrationQueryEntity()
  );
  public selectDetail = new BehaviorSubject<DetailSelectionType>({});
  public syncFilterOptions = new BehaviorSubject<Partial<FilterOptionsState>>(
    {}
  );
  public checkStatus = new BehaviorSubject<string>("");
  public fetchPartners = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStores = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStaff = new BehaviorSubject<BaseUserRequestModel>({});
  public loadUsers = new BehaviorSubject<BaseUserRequestModel>({});

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchCarrier.complete();
    this.fetchPackage.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.selectDetail.complete();
    this.syncFilterOptions.complete();
    this.checkStatus.complete();
    this.fetchPartners.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
    this.loadUsers.complete();
  }
}

class PackageRegistrationOutput implements Disposable {
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public onCheckStatusSuccess = new BehaviorSubject<boolean>(false);
  public onDetailLoaded = new BehaviorSubject<{ action?: string } | null>(null);
  public onPackageLoaded = new BehaviorSubject<{
    page: number;
    count: number;
    code?: string;
  } | null>(null);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onCheckStatusSuccess.complete();
    this.onDetailLoaded.complete();
    this.onPackageLoaded.complete();
  }
}

@injectable()
export class PackageRegistrationViewModel extends BasedObservedViewModel<
  PackageRegistrationInput,
  PackageRegistrationOutput,
  IPackageRegistrationObservedState
> {
  private packageRegistrationUseCase =
    PackageRegistrationInjection.getPackageRegistrationUseCase();

  protected override createObservedState(): IPackageRegistrationObservedState {
    return {
      list: [],
      detail: {},
      pagination: { page: 0, total: 0, limit: 10 },
      loading: false,
      detailLoading: false,
      statusLoading: {},
      carrier: [],
      packageList: [],
      partnerOptions: [],
      storeOptions: [],
      staffOptions: [],
      userOptions: [{ label: strings.GlobalLabels.All, value: "" }],
      summary: {},
    };
  }

  constructor() {
    super(new PackageRegistrationInput(), new PackageRegistrationOutput());
  }

  private downloadExcel(data: Blob): void {
    const url = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    const timestamp = dayjs().format("YYYYMMDD_HHmmss");
    link.href = url;
    link.setAttribute("download", `PACKAGE_REPORT_${timestamp}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  private toPartnerOptions(items: any[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({
        label: `${item.name}${
          item.providerCode ? ` - ${item.providerCode.toUpperCase()}` : ""
        }`,
        value: item.code,
        type: item.type,
        isStore: item.isStore,
        providerCode: item.providerCode,
      })),
    ];
  }

  private toStoreOptions(items: any[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({
        label: item.store_name,
        value: item.store_code,
      })),
    ];
  }

  private toUserOptions(items: BaseUserResponseModel[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({ label: item.name, value: item.id })),
    ];
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params, guard) => {
      try {
        guard?.runIfLatest(() => {
          this.setObservedState({
            loading: true,
            list: [],
            summary: {},
            pagination: {
              page: params.page ?? this.observedState.pagination.page,
              limit: params.limit ?? this.observedState.pagination.limit,
              total: this.observedState.pagination.total,
            },
          });
        });
        const res = await this.packageRegistrationUseCase.getList(params);
        guard?.runIfLatest(() => {
          if (res?.data) {
            this.setObservedState({
              list: res.data,
              pagination: res.pagination ?? {},
              summary: res.summary ?? {},
            });
          }
        });
      } catch (error) {
        console.error(error);
        guard?.runIfLatest(() => this.setObservedState({ list: [] }));
        this.output.onError.next(error as Error);
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.fetchPackage, async (params, guard) => {
      try {
        const res = await this.packageRegistrationUseCase.getFilterList(
          params,
          "package"
        );
        const page = params.page ?? 0;
        const uniqueData = Array.from(
          new Map((res ?? []).map((item) => [item.code, item])).values()
        );
        guard?.runIfLatest(() => {
          const packageList =
            page > 0
              ? Array.from(
                  new Map(
                    [...this.observedState.packageList, ...uniqueData].map(
                      (item) => [item.code, item]
                    )
                  ).values()
                )
              : uniqueData;
          this.setObservedState({ packageList });
          this.output.onPackageLoaded.next({
            page,
            count: uniqueData.length,
            code: params.code,
          });
        });
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchPartners, async (params) => {
      try {
        const cache = productCache.getCache(CacheKeyEnum.partnerList);
        if (!isEmpty(cache)) {
          this.setObservedState({
            partnerOptions: this.toPartnerOptions(cache),
          });
          return;
        }
        const res = await this.packageRegistrationUseCase.getFilterList(
          params,
          "partner"
        );
        const data = res ?? [];
        this.setObservedState({
          partnerOptions: this.toPartnerOptions(data),
        });
        productCache.saveCache({
          key: CacheKeyEnum.partnerList,
          product: data,
        });
      } catch (error) {
        console.error("Error fetching partners:", error);
        this.setObservedState({ partnerOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStores, async (params) => {
      try {
        if (!params.partnerCode) return;
        const res = await this.packageRegistrationUseCase.getFilterList(
          params,
          "store"
        );
        this.setObservedState({
          storeOptions: this.toStoreOptions(res ?? []),
        });
      } catch (error) {
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStaff, async (params) => {
      if (isEmpty(params.storeCode)) {
        this.setObservedState({ staffOptions: [] });
        return;
      }
      try {
        const res = await this.packageRegistrationUseCase.getUserForFilter(
          params,
          "staff"
        );
        const allUsers = (res ?? []).flatMap((item: any) => item?.users ?? []);
        const mappedUsers = allUsers.map((user: any) => ({
          id: user.id,
          name: user.name || user.userName || user.email || user.phone || "",
          code: user.code,
        }));
        this.setObservedState({
          staffOptions: this.toUserOptions(mappedUsers),
        });
      } catch (error) {
        this.setObservedState({ staffOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadUsers, async (params) => {
      try {
        const filterType =
          params.providerCode === "mobitopup" ? "staff" : "userB2B";
        const res = await this.packageRegistrationUseCase.getUserForFilter(
          params,
          filterType
        );
        const allUsers = (res ?? []).flatMap((item: any) => item?.users ?? []);
        const mappedUsers = allUsers.map((user: any) => ({
          id: user.id,
          name: user.fullName || user.email || user.name || user.phone || "",
          code: user.code,
        }));
        this.setObservedState({
          userOptions: this.toUserOptions(mappedUsers),
        });
      } catch (error) {
        this.setObservedState({ userOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const res = await this.packageRegistrationUseCase.getFilterList(
          params,
          "carrier"
        );
        this.setObservedState({ carrier: res ?? [] });
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.setObservedState({ detailLoading: true });
        const res = await this.packageRegistrationUseCase.getDetail(params);
        if (res?.data) {
          this.setObservedState({ detail: res.data });
          this.output.onDetailLoaded.next({});
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        this.setObservedState({ detailLoading: false });
      }
    });

    this.skipFirstInitial(this.input.selectDetail, (selection) => {
      if ("action" in selection) {
        this.setObservedState({ detail: selection.record });
        this.output.onDetailLoaded.next({ action: selection.action });
        return;
      }
      this.setObservedState({ detail: selection });
      this.output.onDetailLoaded.next({});
    });

    this.skipFirstInitial(this.input.syncFilterOptions, (options) => {
      this.setObservedState(options);
    });

    this.skipFirstInitial(this.input.exportExcel, async (params, guard) => {
      try {
        guard?.runIfLatest(() => this.setObservedState({ loading: true }));
        const res = await this.packageRegistrationUseCase.exportReport(params);
        if (res?.data) {
          if (res.data.size < 1000) {
            this.output.onError.next({
              message: strings.GlobalMessage.ExcelError,
            });
            return;
          }
          this.downloadExcel(res.data);
          this.output.onExportSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.checkStatus, async (code) => {
      if (!code) return;
      try {
        this.setObservedState((previous) => ({
          ...previous,
          statusLoading: { ...previous.statusLoading, [code]: true },
        }));
        const res = await this.packageRegistrationUseCase.checkStatus(code);
        if (res?.data) {
          this.output.onCheckStatusSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        this.setObservedState((previous) => ({
          ...previous,
          statusLoading: { ...previous.statusLoading, [code]: false },
        }));
      }
    });
  }
}
